import { Editor, Transforms, Text, Range } from 'vendor/slate'
import { COMMENT_ROOT, getCommentKey, isCommentKey, getThreadIdFromKey } from './constants'

// Comment threads are stored as text-leaf marks, mirroring Plate's inline
// comments: a `comment: true` flag plus one `comment_<threadId>` mark per thread
// that overlaps the leaf. The threadId doubles as the comment record's
// `references` value so the existing GraphQL comment store needs no changes.

export const getCommentThreadIdsFromLeaf = leaf => {
  if (!leaf || !leaf[COMMENT_ROOT]) return []
  return Object.keys(leaf)
    .filter(isCommentKey)
    .map(getThreadIdFromKey)
    .filter(Boolean)
}

export const leafHasComment = leaf => getCommentThreadIdsFromLeaf(leaf).length > 0

export const addCommentMark = (editor, range, threadId, userId) => {
  if (!range || Range.isCollapsed(range) || !threadId) return

  Transforms.setNodes(
    editor,
    {
      [COMMENT_ROOT]: true,
      [getCommentKey(threadId)]: { id: threadId, userId: userId || null }
    },
    { at: range, match: Text.isText, split: true }
  )
}

// Remove a single thread's mark everywhere it appears, clearing the shared
// `comment` flag on leaves that no longer belong to any thread.
export const removeCommentMark = (editor, threadId) => {
  if (!threadId) return
  const key = getCommentKey(threadId)

  Editor.withoutNormalizing(editor, () => {
    const matches = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: node => Text.isText(node) && !!node[key]
      })
    )

    for (const [node, path] of matches) {
      Transforms.unsetNodes(editor, key, { at: path })

      const stillCommented = Object.keys(node).some(k => isCommentKey(k) && k !== key)
      if (!stillCommented) {
        Transforms.unsetNodes(editor, COMMENT_ROOT, { at: path })
      }
    }
  })
}

export const findCommentThreadIds = editor => {
  const ids = new Set()

  for (const [node] of Editor.nodes(editor, {
    at: [],
    match: node => Text.isText(node) && !!node[COMMENT_ROOT]
  })) {
    getCommentThreadIdsFromLeaf(node).forEach(id => ids.add(id))
  }

  return Array.from(ids)
}
